import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import { FormField } from "../../../../Components/FormField";
import TextInput from "../../../../Components/TextInput";
import SelectDropdown from "../../../../Components/Dropdown/SelectDropdown";
import SidebarField from "../../../../Components/SidebarField";
import Button from "../../../../Components/Button";
import { Checkbox } from "primereact/checkbox";
import { MdDelete } from "react-icons/md";
import { FaEdit } from "react-icons/fa";
import { toast } from "react-toastify";

import { approvalRoutes } from "../../../../Router/router";
import { useFetchAll } from "../../../../custom/FetchApiHook";
import { UsersApi } from "../../../../App/api/users.api";
import { multilevelApprovalApi } from "../../../../App/api/multilevelApproval.api";
import DeleteFieldModal from "../../../../Components/Common/DeleteFieldModal";
import { useSelector } from "react-redux";

const MultiLevelApproval = () => {
  const { theme } = useSelector((state) => state.theme);
  const [openSidebar, setOpenSidebar] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /* ---------------- Fetch Data ---------------- */

  const pagination = {
    page: 1,
    limit: 10000,
    sortOrder: "desc",
    filter: { status: "Approved" },
  };

  const { value: usersData } = useFetchAll(() =>
    UsersApi.getAllUsers(pagination)
  );

  const { value: allApprovals, fetchAllData: getAllApprovals } = useFetchAll(
    () => multilevelApprovalApi.getAllApprovals()
  );

  useEffect(() => {
    getAllApprovals();
  }, []);

  /* ---------------- Options ---------------- */

  const getUserName = (userId) => {
    const user = usersOptions.find((u) => u._id === userId);
    return user ? user.name : "-";
  };

  const usersOptions =
    usersData?.data?.user?.map((u) => ({
      _id: u._id,
      name: `${u.first_name} ${u.last_name}`,
    })) || [];

  const componentOptions = approvalRoutes.map((r) => r.meta);

  const levelOptions = Array.from({ length: 20 }, (_, i) => ({
    name: `Level-${i + 1}`,
  }));

  /* ---------------- Handlers ---------------- */

  const handleCreate = () => {
    setEditData(null);
    setOpenSidebar(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    console.log("row clicked:", row);
    setOpenSidebar(true);
  };

  const handleClose = () => {
    setEditData(null);
    setOpenSidebar(false);
  };

  const handleDelete = async () => {
    try {
      await multilevelApprovalApi.deleteApprovals(deleteId);
      toast.success("Approval deleted");
      getAllApprovals();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      name: values.name,
      component: values.component,
      active: values.active,
      approvalFlow: values.approvalFlow.map(({ isNew, ...rest }) => rest),
    };

    try {
      if (editData?._id) {
        await multilevelApprovalApi.updateApproval(payload, editData._id);
        toast.success("Approval updated");
      } else {
        await multilevelApprovalApi.createApproval(payload);
        toast.success("Approval created");
      }

      handleClose();
      getAllApprovals();
    } catch {
      toast.error("Something went wrong");
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="p-6 bg-[--bg-color] h-[95vh] overflow-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Multi Level Approval</h2>
        <button onClick={handleCreate} className="btn">
          Create Approval
        </button>
      </div>

      {/* ---------------- TABLE ---------------- */}
      <div className="shadow-md rounded-lg overflow-x-auto">
        <table className="w-full border-collapse border">
          <thead className="bg-[var(--bg-tbl-header)]">
            <tr>
              <th className="border p-2">S.No</th>
              <th className="border p-2">Name</th>
              <th className="border p-2">Component</th>
              <th className="border p-2">Status</th>
              <th className="border p-2 sticky right-0">Actions</th>
            </tr>
          </thead>
          <tbody>
            {JSON.stringify(theme)}
            {allApprovals?.data?.length ? (
              allApprovals.data.map((d, i) => (
                <tr
                  key={d._id}
                  className={`${theme ? "text-gray-900" : "text-gray-200"}`}
                >
                  <td
                    className={`border text-center p-2 ${
                      theme ? "text-gray-900" : "text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </td>
                  <td
                    className={`border text-center p-2 ${
                      theme ? "text-gray-900" : "text-gray-200"
                    }`}
                  >
                    {d.name}
                  </td>
                  <td
                    className={`border text-center p-2 ${
                      theme ? "text-gray-900" : "text-gray-200"
                    }`}
                  >
                    {d.component}
                  </td>
                  <td
                    className={`border text-center p-2 ${
                      theme ? "text-gray-900" : "text-gray-200"
                    }`}
                  >
                    {d.active ? "Active" : "Inactive"}
                  </td>
                  <td className="border sticky right-0 bg-[--bg-color] p-2">
                    <div className="flex justify-center items-center gap-3">
                      {/* <button
                        onClick={() => {
                          setDeleteId(d._id);
                          setDeleteModal(true);
                        }}
                        className="btn-primary w-9 h-9 rounded-full flex items-center justify-center"
                      >
                        <MdDelete />
                      </button> */}

                      <button
                        onClick={() => handleEdit(d)}
                        className="btn-primary w-9 h-9 rounded-full flex items-center justify-center"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center p-4">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---------------- SIDEBAR ---------------- */}
      {openSidebar && (
        <Formik
          enableReinitialize
          initialValues={{
            name: editData?.name || "",
            component: editData?.component || "",
            active: editData?.active || false,
            approvalData: {
              approval: "",
              level: "",
              user: "",
            },
            approvalFlow:
              editData?.approvalFlow?.map((r) => ({
                approval: r.approval,
                level: r.level,
                user: typeof r.user === "object" ? r.user._id : r.user,
                isNew: false,
              })) || [],
          }}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => {
            const isDuplicate = values.approvalFlow.some(
              (item) =>
                item.approval === values.approvalData.approval &&
                item.level === values.approvalData.level &&
                item.user === values.approvalData.user
            );

            const canAdd =
              values.approvalData.approval &&
              values.approvalData.level &&
              values.approvalData.user &&
              !isDuplicate;
            return (
              <Form>
                <SidebarField
                  handleClose={handleClose}
                  title={editData?._id ? "Update Approval" : "Add New Approval"}
                  button1={
                    <Button text="Cancel" type="button" onClick={handleClose} />
                  }
                  button2={
                    <Button
                      text={editData?._id ? "Update" : "Create"}
                      type="submit"
                    />
                  }
                >
                  {/* Approval Info */}
                  <div className="border rounded-lg p-4 mb-4">
                    <h2 className="font-semibold mb-3">Approval Info</h2>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField label="Approval Name">
                        <TextInput
                          value={values.name}
                          onChange={(e) =>
                            setFieldValue("name", e.target.value)
                          }
                        />
                      </FormField>

                      <FormField label="Component">
                        <select
                          value={values.component || ""}
                          onChange={(e) =>
                            setFieldValue("component", e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="" disabled>
                            Select Component
                          </option>

                          {componentOptions.map((item) => (
                            <option
                              key={item.entityType}
                              value={item.entityType}
                            >
                              {item.entityType}
                            </option>
                          ))}
                        </select>
                      </FormField>
                    </div>

                    <div className="flex items-center gap-2 mt-3">
                      <input
                        type="checkbox"
                        checked={values.active}
                        onChange={() => setFieldValue("active", !values.active) }
                      />
                      <span>Active</span>
                    </div>
                  </div>

                  {/* Approval Definition */}
                  <div className="border rounded-lg p-4">
                    <h2 className="font-semibold mb-3">
                      Approval Definition Info
                    </h2>

                    <div className="grid grid-cols-3 gap-4">
                      {/* Mandatory Type */}
                      <select
                        value={values.approvalData.approval || ""}
                        onChange={(e) =>
                          setFieldValue("approvalData.approval", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="" disabled>
                          Select Mandatory Type
                        </option>
                        <option value="Mandatory">Mandatory</option>
                        <option value="Non-Mandatory">Non-Mandatory</option>
                      </select>

                      {/* Level */}
                      <select
                        value={values.approvalData.level || ""}
                        onChange={(e) =>
                          setFieldValue("approvalData.level", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="" disabled>
                          Select Level
                        </option>
                        {levelOptions.map((lvl) => (
                          <option key={lvl.name} value={lvl.name}>
                            {lvl.name}
                          </option>
                        ))}
                      </select>

                      {/* User */}
                      <select
                        value={values.approvalData.user || ""}
                        onChange={(e) =>
                          setFieldValue("approvalData.user", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="" disabled>
                          Select User
                        </option>
                        {usersOptions.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {isDuplicate && (
                      <p className="text-red-500 pt-1">
                        combination already exisit...!
                      </p>
                    )}
                    <button
                      disabled={!canAdd}
                      className={`btn mt-4 ${
                        !canAdd ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();

                        const { approval, level, user } = values.approvalData;

                        const isDuplicate = values.approvalFlow.some(
                          (item) =>
                            item.approval === approval &&
                            item.level === level &&
                            item.user === user
                        );

                        if (isDuplicate) {
                          toast.error(
                            "This approval combination already exists"
                          );
                          return;
                        }

                        setFieldValue("approvalFlow", [
                          ...values.approvalFlow,
                          {
                            approval,
                            level,
                            user,
                            isNew: true,
                          },
                        ]);

                        setFieldValue("approvalData", {
                          approval: "",
                          level: "",
                          user: "",
                        });
                      }}
                    >
                      Add
                    </button>

                    {/* Table */}
                    <div className="mt-6">
                      <table className="w-full border">
                        <thead>
                          <tr>
                            <th className="border p-2">S.No</th>
                            <th className="border p-2">Approval</th>
                            <th className="border p-2">Level</th>
                            <th className="border p-2">User</th>
                            <th className="border p-2">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {values.approvalFlow.map((r, i) => (
                            <tr key={i}>
                              <td className="border p-2 text-center">
                                {i + 1}
                              </td>
                              <td className="border p-2 text-center">
                                {r.approval}
                              </td>
                              <td className="border p-2 text-center">
                                {r.level}
                              </td>
                              <td className="border p-2 text-center">
                                {getUserName(r.user)}
                              </td>

                              <td className="border p-2 text-center">
                                {r.isNew && (
                                  <button
                                    className="text-red-600"
                                    onClick={() => {
                                      const copy = [...values.approvalFlow];
                                      copy.splice(i, 1);
                                      setFieldValue("approvalFlow", copy);
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </SidebarField>
              </Form>
            );
          }}
        </Formik>
      )}

      {deleteModal && (
        <DeleteFieldModal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default MultiLevelApproval;
